import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { ToggleButtons } from "../../../../../../MainComponents/ToggleButton/ToggleButton";
import { getInvestigation } from "../../../../../../../apis";
import { getInvestigationProposals } from "../../../../../../../helpers/correctiveActions";
import { getInvestigationLabel } from "../../../../../../../helpers/investigationLabel";

// Lo que cada opcion del selector le hace a la propuesta. Al salir de "descartar"
// se limpia el motivo para no dejar texto huerfano en el registro.
const DECISIONS = {
  apply: { confirmed: true, reason: "" },
  pending: { confirmed: null, reason: "" },
  discard: { confirmed: false },
};

const getDecision = ({ confirmed }) => {
  if (confirmed === false) return "discard";
  return confirmed == null ? "pending" : "apply";
};

/**
 * Trae al flujo de atencion lo que los investigadores propusieron en sus
 * conclusiones.
 *
 * Se listan TODAS las investigaciones que aplican al caso, cada una con sus
 * propuestas, porque el resolutor decide propuesta por propuesta y puede tomar
 * de varias a la vez. Llegan confirmadas y las puede descartar indicando el
 * motivo; lo que quiera agregar por su cuenta va por el selector de catalogo,
 * fuera de este componente.
 *
 * La consulta es diferida: se dispara al montar, que en sanciones ocurre
 * despues de elegir al involucrado.
 *
 * @param type Tipo de accion a traer (measure | sanction)
 * @param involvedName Solo para sanciones: filtra por la persona seleccionada
 * @param proposals Propuestas con su estado: [{ id, kind, confirmed, reason, ... }]
 * @param savedStates Al editar un registro, el estado con el que quedo cada
 *   propuesta la vez anterior (ver getSavedProposalStates). Las que no aparezcan
 *   ahi son nuevas desde entonces y entran con `defaultConfirmed`.
 * @param allowPending Ofrece dejar una propuesta pendiente para otro registro de
 *   la misma persona, ademas de aplicarla o descartarla.
 * @param defaultConfirmed Estado inicial de lo que no tiene decision guardada:
 *   true (aplicada) o null (pendiente).
 * @param resolvedKeys Propuestas que ya se decidieron en otros registros de la
 *   persona: ya no se ofrecen.
 */
export const InvestigationProposals = ({
  investigations = [],
  type,
  involvedName,
  proposals = [],
  onProposalsChange,
  showErrors = false,
  savedStates,
  allowPending = false,
  defaultConfirmed = true,
  resolvedKeys = [],
}) => {
  const [t] = useTranslation("report");
  // El nombre de la investigacion se arma con el namespace "information", que
  // es donde viven "avanzado" y "basico".
  const [tInfo] = useTranslation("information");
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  // Investigaciones ya consultadas, con sus propuestas aplicables. Solo definen
  // que renglon va en que bloque; el estado de cada uno vive en el padre.
  const [groups, setGroups] = useState([]);
  // Evita repetir la consulta de una investigacion ya traida en esta sesion del
  // formulario (el usuario suele ir y venir entre involucrados).
  const cache = useRef({});

  const aliases = investigations.map((item) => item.alias).join("|");
  // Solo se recarga si cambia el conjunto de estados guardados, no su identidad
  // de objeto (el padre lo recalcula en cada render).
  const savedStatesKey = JSON.stringify(savedStates || {});
  const resolvedKeysKey = resolvedKeys.join("|");

  useEffect(() => {
    let mounted = true;

    if (investigations.length === 0) {
      setGroups([]);
      onProposalsChange?.([]);
      return undefined;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const details = await Promise.all(
          investigations.map(async (item) => {
            if (!cache.current[item.alias]) {
              // getInvestigation responde envuelta: { message, response, status }
              cache.current[item.alias] = (
                await getInvestigation(item.alias)
              )?.response;
            }
            return { item, detail: cache.current[item.alias] };
          })
        );
        if (!mounted) return;

        const nextGroups = details.map(({ item, detail }) => {
          // Snapshot con lo necesario para reconstruir el nombre al mostrar el
          // registro, sin volver a consultar la investigacion.
          const investigation = {
            alias: item.alias,
            id: item.id ?? null,
            title: item.title || "",
            advance: item.advance ?? false,
            created_at: item.created_at ?? null,
          };
          const applicable = getInvestigationProposals(detail, {
            investigation,
            type,
            involvedName,
          });
          const available = applicable.filter(
            (proposal) => !resolvedKeys.includes(proposal.key)
          );
          return {
            investigation,
            // Para distinguir "no propuso nada" de "ya se decidio todo".
            allResolved: applicable.length > 0 && available.length === 0,
            proposals: available.map((proposal) => ({
              ...proposal,
              confirmed: defaultConfirmed,
              reason: "",
              ...(savedStates?.[proposal.key] || {}),
            })),
          };
        });

        setGroups(nextGroups);
        onProposalsChange?.(nextGroups.flatMap((group) => group.proposals));
      } catch {
        if (mounted) {
          setGroups([]);
          onProposalsChange?.([]);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // onProposalsChange se deja fuera a proposito: es el emisor del resultado,
    // no una entrada. Incluirlo haria que un padre que pase una lambda inline
    // reejecute la consulta en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliases, involvedName, type, savedStatesKey, resolvedKeysKey, defaultConfirmed]);

  const updateProposal = (id, changes) => {
    onProposalsChange?.(
      proposals.map((proposal) =>
        proposal.id === id ? { ...proposal, ...changes } : proposal
      )
    );
  };

  const getProposal = (id) => proposals.find((item) => item.id === id);

  const getProposalLabel = (proposal) =>
    proposal.kind === "free" ? proposal.text : proposal.label;

  if (investigations.length === 0) return null;

  if (isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2">
          {`${t("Resolution.loading_proposals")}...`}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600} color="secondary">
        {t("Resolution.proposed_by_investigator")}
      </Typography>

      {allowPending && (
        <Typography
          component="div"
          fontSize={11}
          lineHeight={1.4}
          color={theme.palette.blueGray500.main}
        >
          {t("Resolution.proposals_pending_hint")}
        </Typography>
      )}

      {groups.map((group) => (
        <Box
          key={group.investigation.alias}
          sx={{
            border: `1px solid ${theme.palette.blueGray100.main}`,
            borderRadius: 1.5,
            p: 1.5,
          }}
        >
          <Typography
            component="div"
            fontSize={12}
            lineHeight={1.4}
            fontWeight={700}
            color={theme.palette.blueGray600.main}
            mb={0.5}
          >
            {getInvestigationLabel(group.investigation, tInfo)}
          </Typography>

          {group.proposals.length === 0 ? (
            <Typography
              component="div"
              fontSize={11}
              lineHeight={1.4}
              fontStyle="italic"
              color={theme.palette.blueGray500.main}
            >
              {group.allResolved
                ? t("Resolution.proposals_all_resolved")
                : t("Resolution.no_proposals_in_investigation")}
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {group.proposals.map((base) => {
                const proposal = getProposal(base.id) || base;
                const isDiscarded = proposal.confirmed === false;
                const missingReason =
                  isDiscarded && (proposal.reason || "").trim() === "";

                return (
                  <Box key={proposal.id}>
                    <Stack
                      direction={
                        allowPending ? { xs: "column", sm: "row" } : "row"
                      }
                      alignItems="flex-start"
                      spacing={0.5}
                    >
                      {!allowPending && (
                        <Checkbox
                          size="small"
                          checked={!isDiscarded}
                          onChange={(e) =>
                            updateProposal(proposal.id, {
                              confirmed: e.target.checked,
                              // Al reconfirmar se limpia el motivo para no dejar
                              // texto huerfano en el registro.
                              reason: e.target.checked ? "" : proposal.reason,
                            })
                          }
                          sx={{ p: 0.5 }}
                        />
                      )}
                      <Box flex={1} pt={0.25}>
                        <Typography
                          component="div"
                          fontSize={12.8}
                          lineHeight={1.4}
                          fontWeight={600}
                          sx={{
                            textDecoration: isDiscarded
                              ? "line-through"
                              : "none",
                            color: isDiscarded
                              ? theme.palette.blueGray500.main
                              : "inherit",
                          }}
                        >
                          {getProposalLabel(proposal)}
                        </Typography>
                        {proposal.details && (
                          <Typography
                            component="div"
                            fontSize={11}
                            lineHeight={1.4}
                            fontStyle="italic"
                            color={theme.palette.blueGray500.main}
                          >
                            {proposal.details}
                          </Typography>
                        )}
                      </Box>
                      {allowPending && (
                        <ToggleButtons
                          value={getDecision(proposal)}
                          onChange={(decision) =>
                            updateProposal(proposal.id, DECISIONS[decision])
                          }
                          options={[
                            {
                              value: "apply",
                              label: t("Resolution.proposal_apply"),
                            },
                            {
                              value: "pending",
                              label: t("Resolution.proposal_pending"),
                            },
                            {
                              value: "discard",
                              label: t("Resolution.proposal_discard"),
                            },
                          ]}
                          sx={{
                            flexShrink: 0,
                            "& .MuiTypography-root": { fontSize: 12 },
                          }}
                        />
                      )}
                    </Stack>

                    {isDiscarded && (
                      <Box pl={allowPending ? 0 : 4.5} pt={allowPending ? 0.75 : 0} pb={0.75}>
                        <EGInput
                          label={`${t("Resolution.discard_reason")}:`}
                          placeholder={t(
                            "Resolution.discard_reason_placeholder"
                          )}
                          value={proposal.reason || ""}
                          onChange={(e) =>
                            updateProposal(proposal.id, {
                              reason: e.target.value,
                            })
                          }
                          isValid={
                            showErrors && missingReason ? false : undefined
                          }
                          inputProps={{ maxLength: 250 }}
                          fullWidth
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
};
